import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import AdminLayout from "../../layout/AdminLayout";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/CandidatesModel";
import { useNotificationCount } from "../../../context/NotificationCountContext";
import CandidateTable from "./CandidateTable";
import { ConfirmDelete } from "../../modals/ConfirmDelete";
import { apiError } from "../../../helpers/ErrorHelper";
import { toast } from "react-toastify";
import PageHeader from "../../common/PageHeader";
import config from "../../../config/config";
import { getData as getClients } from '../../../models/ClientsModel';
import { getData as getRequirements } from '../../../models/RequirementsModel';
import { getData as getCandidateStatus } from '../../../models/CandidateStatusModel';
import { getData as getSkills } from '../../../models/SkillsModel';
import { MyReactSelect, MyReactSelectMultiple } from "../../inputs/inputs";
import { CandidateView } from "./CandidateView";
import { useDebouncedCallback } from 'use-debounce';
import { checkPermission } from "../../../helpers/GeneralHelper";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dateFormat from "dateformat";
import { getRecruiters } from '../../../models/UserModel';
import PipelineFilterBar from "../../common/PipelineFilterBar";
import ListFilterActions from "../../common/ListFilterActions";
import { usePipelineUrlFilters } from "../../../hooks/usePipelineUrlFilters";
import { hasPipelineUrlContext } from "../../../helpers/PipelineFilterHelper";

export default function Candidates() {
  const module = {
    title: 'Candidates',
    singularTitle: 'Candidate',
    key: 'candidates',
    route: 'candidates',
    primaryCol: '_candidate_id'
  };

  const { state } = useAuth();
  let user_data = state.user_data;
  const location = useLocation();
  const pipelinePrefill = location.state?.pipelinePrefill;
  const [searchParams] = useSearchParams();
  var _client_id = searchParams.get('_client_id');
  var _requirement_id = searchParams.get('_requirement_id');
  const urlHasContext = hasPipelineUrlContext(searchParams);
  const [recruiters, setRecruiters] = useState([]);

  const navigate = useNavigate();

  const { notificationCount, setNotificationCount } = useNotificationCount();

  const [singleData, setSingleData] = useState({});
  const openUpdateModal = (data) => {
    navigate(`/candidates/${data.candidate_unique_id}/edit`, { 
      state: {
        data: data,
        module: module,
      },
    });
  };
  // Update End

  // Delete Start
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const openDeleteModal = (data) => {
    setSingleData(data);
    setToggleDeleteModal(true);
  };

  const deleteData = async() => {
    setLoading(true);

    try {
      const response = await model.remove(user_data.user_token, singleData[module.primaryCol]);
      if (response.status) {
        setNotificationCount(localStorage.getItem('unread_notification_count'));
        toast.success(response.message);

        model.saveToStorage(response.data[module.key], response.data.pagination);
        refreshData(); 
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);

      if (error.response?.data.invalid_token == 1) {
        navigate('/re-login');
      }
    }

    setToggleDeleteModal(false);
    setLoading(false);
  }
  // Delete End

  const [toggleViewModal, setToggleViewModal] = useState(false);
  const handleView = (data) => {
    // setSingleData(data);
    // setToggleViewModal(true);
    navigate(`/candidates/${data.candidate_unique_id}/view`, { 
      state: {
        data: data,
        module: module,
      },
    });
  }

  // Flush Start
  const flashData = async () => {
    localStorage.removeItem(module.key);
    fetchData(limit, page);
  };
  // Flush End

  // Filter Start
  const [filters, setFilters] = useState({
    candidate_client_id: pipelinePrefill?.clientOption || '',
    candidate_requirement_id: pipelinePrefill?.requirementOption || '',
    candidate_status_id: '',
    candidate_skill_id: [],
    candidate_recruiter_id: '',
    candidate_date_from: '',
    candidate_date_to: '',
  });

  const [clients, setClients] = useState([]);
  const [requirements, setRequirements] = useState(() =>
    pipelinePrefill?.requirementOption ? [pipelinePrefill.requirementOption] : []
  );
  const [canStatus, setCanStatus] = useState([]);
  const [skills, setSkills] = useState([]);
  
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const result = await getClients({user_token: user_data.user_token, page: 1, limit: -1});

        const clientOptions = result.clients.map(client => ({
          value: client._client_id,
          label: client.client_name
        }));

        setClients(clientOptions);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchStatus = async() => {
      try {
        let apiParam = {user_token: user_data.user_token, page: 1, limit: -1};
        const response = await getCandidateStatus(apiParam);

        if (typeof response != typeof undefined) {
          const options = response.candidate_status.map(ele => ({
            value: ele._status_id,
            label: ele.status_name
          }));

          setCanStatus(options);
        }
      } catch (error) {
          console.error(error);
      }
    }

    const fetchSkills = async() => {
      try {
        let apiParam = {user_token: user_data.user_token, page: 1, limit: -1};
        const response = await getSkills(apiParam);

        if (typeof response != typeof undefined) {
          const options = response.skills.map(ele => ({
            value: ele._skill_id,
            label: ele.skill_name
          }));

          setSkills(options);
        }
      } catch (error) {
        console.error(error);
      }
    }
    const fetchRecruiters = async() => {
      try {
        let apiParam = {user_token: user_data.user_token, page: 1, limit: -1};
        const response = await getRecruiters(apiParam);
        console.log('recruiters => ',response);
        if (typeof response != typeof undefined) {
          let options = [];
          response.recruiters.forEach(function(item) {
            options.push({
              value: item._user_id,
              label: item.user_first_name + ' ' + item.user_last_name
            });
          });

          setRecruiters(options);

        }
      } catch (error) {
        console.error(error);
      }
    }

    fetchClients();
    fetchStatus();
    fetchSkills();
    fetchRecruiters();
  }, []);

  const applyRequirementOptions = useCallback((prefill) => {
    if (prefill?.requirementOption) {
      setRequirements((prev) => {
        const exists = prev.some(
          (item) => item.value === prefill.requirementOption.value
        );
        return exists ? prev : [prefill.requirementOption, ...prev];
      });
    }
  }, []);

  const { chips, clearUrlContext, urlSynced } = usePipelineUrlFilters({
    user_token: user_data.user_token,
    searchParams,
    navigate,
    routePath: "/candidates",
    clientColumn: "candidate_client_id",
    requirementColumn: "candidate_requirement_id",
    setFilters,
    clients,
    onPrefillApplied: applyRequirementOptions,
  });

  useEffect(() => {
    const clientId = filters.candidate_client_id?.value;
    const selectedRequirement = filters.candidate_requirement_id;

    if (!clientId) {
      setRequirements([]);
      if (selectedRequirement) {
        setFilters((prev) => ({ ...prev, candidate_requirement_id: '' }));
      }
      return;
    }

    const fetchRequirements = async () => {
      try {
        const response = await getRequirements({
          user_token: user_data.user_token,
          requirement_client_id: clientId,
          page: 1,
          limit: -1,
        });

        if (typeof response === "undefined") return;

        const options = (response.requirements || []).map((item) => ({
          value: item._requirement_id,
          label: item.requirement_name,
        }));

        setRequirements(options);

        if (selectedRequirement?.value) {
          const stillValid = options.some(
            (item) => String(item.value) === String(selectedRequirement.value)
          );
          if (!stillValid) {
            setFilters((prev) => ({ ...prev, candidate_requirement_id: '' }));
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchRequirements();
  }, [filters.candidate_client_id?.value, user_data.user_token]);

  const [isFilterReset, setIsFilterReset] = useState(false);
  const [isTrash, setIsTrash] = useState(false);
  useEffect(() => {
    if (urlHasContext && !urlSynced) return;

    setIsFilterReset(false);
    Object.keys(filters).forEach(item => {
      let isArray = Array.isArray(filters[item]);
      if ((isArray && filters[item].length > 0) || (! isArray && filters[item])) {
        setIsFilterReset(true);
        return false;
      }
    });

    fetchData(10, 1);
  }, [filters, isTrash, urlSynced, urlHasContext]);

  const resetFilter = () => {
    let blankFilter = {};
    Object.keys(filters).forEach(item => {
      blankFilter[item] = item === 'candidate_skill_id' ? [] : '';
    });
    setFilters(blankFilter);
  }
  // Filter End

  // List Start
  const [listData, setListData] = useState([]);
  const [cacheTime, setCacheTime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTrash = (isTrash) => {
    setIsTrash(isTrash);
  }

  const debounced = useDebouncedCallback(() => fetchData(limit, page), 500);

  const fetchData = async (newLimit, page) => {
    setLoading(true);

    if(filters.candidate_client_id){
      _client_id = filters.candidate_client_id.value;
    }
    if(filters.candidate_requirement_id){
      _requirement_id = filters.candidate_requirement_id.value;
    }
    
    let filterSkills = [];
    for (let skill of filters?.candidate_skill_id) {
      filterSkills.push(skill?.value);
    }

    try {
      let apiParam = {
        search: searchQuery,
        page: page,
        limit: newLimit,
        user_token: user_data.user_token,
        candidate_client_id: _client_id ? _client_id : '',
        candidate_requirement_id: _requirement_id ? _requirement_id : '',
        candidate_status_id: filters?.candidate_status_id?.value ?? '',
        candidate_skill_id: filterSkills.join(','),
        isTrash: isTrash ? 1 : 0,
        include_inactive_mappings: 1, // include inactive mappings
        candidate_recruiter_id: filters?.candidate_recruiter_id?.value ?? '',
        date_from: filters.candidate_date_from || '',
        date_to: filters.candidate_date_to || '',
      };
      const response = await model.getData(apiParam);
      if (typeof response == typeof undefined) {
        navigate("/re-login");
        return;
      }

      setListData(response[module.key]);
      setTotalRows(response.pagination.total_rows);
      setLimit(newLimit);
      setPage(page);

      setNotificationCount(localStorage.getItem("unread_notification_count"));

      if (response.time) {
        setCacheTime(response.time);
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };
  // List End

  const refreshData = async () => {
    fetchData(limit, page);
  }

  const getTimeDifference = (date) => {
    if (!date || date === "0000-00-00 00:00:00") return "N/A";

    const now = new Date();
    const updated = new Date(date);
    const diffMs = now - updated;

    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffMins > 0) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;

    return "Just now";
  };


  const exportToExcel = async () => {
    if (!listData || listData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    // Generate timestamp (YYYYMMDD_HHMMSS + random 4 digits)
    const timestamp = new Date().toISOString()
                                .replace(/[-:T]/g, "") // remove special chars
                                .split(".")[0];        // remove milliseconds

    const fileName = `${module.title}_${timestamp}.xlsx`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`${module.title}`);

    // Define headers and style
    const headers = [
      { header: "Candidate", key: "candidate_name" },
      { header: "Client", key: "client_name" },
      { header: "Requirement", key: "requirement_name" },
      
      { header: "Mobile", key: "candidate_mobile" },
      { header: "Email", key: "candidate_email" },
      { header: "Recruiter", key: "recruiter_name" },
      { header: "Status", key: "status_name" },
      { header: "Last Updated", key: "candidate_update_time" },
    ];

    //worksheet.columns = headers;
    worksheet.columns = [
      { header: "Candidate", key: "candidate_name" },
      { header: "Client", key: "client_name" },
      { header: "Requirement", key: "requirement_name" },
      
      { header: "Mobile", key: "candidate_mobile" },
      { header: "Email", key: "candidate_email" },
      { header: "Recruiter", key: "recruiter_name" },
      { header: "Status", key: "status_name" },
      { header: "Last Updated", key: "candidate_update_time" },
    ];

    // Add rows
    listData.forEach(row => {
      worksheet.addRow({
        client_name: row.client_name,
        requirement_name: row.requirement_name || "N/A",
        candidate_name: row.candidate_firstname + ' ' + row.candidate_lastname || "N/A",
        candidate_mobile: row.candidate_mobile || "N/A",
        candidate_email: row.candidate_email || "N/A",
        recruiter_name: row.recruiter_name || "N/A",
        status_name: row.status_name || "N/A",
        candidate_update_time: getTimeDifference(row.candidate_update_time || row.candidate_create_time),
      });
    });

    // Style header
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'D9E1F2' }, // Light blue
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Generate Excel file and save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>{module.title} - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid cms-list-shell">
          <PageHeader module={module} isTrash={isTrash} toggleTrash={fetchTrash} />

          {!isTrash && (
            <PipelineFilterBar
              chips={chips}
              onClearAll={clearUrlContext}
              workflowStage="candidates"
              compactHelp
            />
          )}

          {/* <!-- Start::row-1 --> */}
          <div className="row">
            <div className="col-xl-12">
              <div className="card custom-card cms-list-card">
                {! isTrash && <div className="card-header cms-list-toolbar d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="card-title fw-normal mb-0" style={{textTransform: 'unset'}}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <MyReactSelect column="candidate_client_id" options={clients} firstOption="Select client" fields={filters} setFields={setFilters} />
                      <MyReactSelect column="candidate_requirement_id" options={requirements} firstOption="Select Requirement" fields={filters} setFields={setFilters} />
                      <MyReactSelect column="candidate_status_id" options={canStatus} firstOption="Select Status" fields={filters} setFields={setFilters} />
                      <MyReactSelectMultiple column="candidate_skill_id" options={skills} firstOption="Select Skills" fields={filters} setFields={setFilters} />
                      <MyReactSelect column="candidate_recruiter_id" options={recruiters} firstOption="Select Recruiter" fields={filters} setFields={setFilters} />
                      <span className="text-muted small align-self-center">Added</span>
                      <input
                        type="date"
                        className="form-control form-control-sm cms-list-date"
                        title="Created from"
                        value={filters.candidate_date_from}
                        onChange={(e) => setFilters({ ...filters, candidate_date_from: e.target.value })}
                      />
                      <span className="text-muted small align-self-center">—</span>
                      <input
                        type="date"
                        className="form-control form-control-sm cms-list-date"
                        title="Created to"
                        value={filters.candidate_date_to}
                        min={filters.candidate_date_from || undefined}
                        onChange={(e) => setFilters({ ...filters, candidate_date_to: e.target.value })}
                      />
                      <ListFilterActions
                        showExport={listData.length > 0}
                        onExport={exportToExcel}
                        showReset={isFilterReset}
                        onReset={resetFilter}
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-wrap align-items-center gap-2 cms-list-search-actions">
                    <input type="text" className="form-control table-search cms-list-search" onChange={(e) => {debounced(e.target.value); setSearchQuery(e.target.value);}} placeholder="Search..." autoComplete='off' />

                      {checkPermission(module.route, 'add') && 
                        <button className="btn btn-primary btn-sm cms-list-btn" onClick={() => navigate(`/candidates/create`, { 
                          state: { 
                            module,
                            prefill: filters.candidate_client_id?.value ? {
                              clientId: filters.candidate_client_id.value,
                              clientName: filters.candidate_client_id.label,
                              requirementId: filters.candidate_requirement_id?.value,
                              requirementName: filters.candidate_requirement_id?.label,
                            } : undefined,
                          },
                        })}>
                          <i className="ri-add-line me-1 fw-semibold align-middle"></i>
                          Add
                        </button>
                      }

                    {config.isCache ? 
                      <button className="btn btn-success-light btn-sm cms-list-btn" onClick={() => flashData()}>
                        <i className="ri-refresh-line me-1 fw-semibold align-middle"></i> Refresh
                      </button> : ''
                    }
                  </div>
                </div>}
                <div className="card-body">
                  <div className="table-responsive">
                    <CandidateTable
                      module={module}
                      data={listData}
                      loading={loading}
                      totalRows={totalRows}
                      page={page}
                      limit={limit}
                      fetchData={fetchData}
                      openUpdateModal={openUpdateModal}
                      openDeleteModal={openDeleteModal}
                      handleView={handleView}
                      isTrash={isTrash}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <!--End::row-1 --> */}

          {/* <!--Delete Modal Start--> */}
          <ConfirmDelete
            module={module}
            showModal={toggleDeleteModal}
            setShowModal={setToggleDeleteModal}
            loading={loading}
            onConfirm={deleteData}
          />
          {/* <!--Delete Modal End--> */}

          {/* View Modal */}
          <CandidateView
            module={module}
            showModal={toggleViewModal}
            setShowModal={setToggleViewModal}
            data={singleData}
          />
        </div>
      </div>

      <Helmet></Helmet>
    </AdminLayout>
  );
}
